import { updatesIndexDescription } from './sortUpdateIndexCards';

/**
 * Public Snowflake update pages.
 * Claims are limited to the Oct 14, 2026 release brief, the behavior-change
 * explainer, and Snowflake pages linked from those documents. October 13–14
 * items that post-date the brief are linked from the Snowflake pages named
 * on each row.
 * The SQLAlchemy 2.0 checklist uses the Oct 19, 2026 SQLAlchemy release notes
 * and the Python Connector 5.0.0 requirement for Python 3.11 or later.
 * The Cortex AI Gateway note uses the Oct 16, 2026 region expansion on the
 * all-release-notes page. The gateway remains the Sep 15, 2026 preview.
 */

export const UPDATES_AS_OF = 'October 16, 2026';

/** Date line for notes added October 19. Earlier notes stay on UPDATES_AS_OF. */
export const OCTOBER_19_NOTE_AS_OF = 'October 19, 2026';
export const SQLALCHEMY_UPGRADE_AS_OF = OCTOBER_19_NOTE_AS_OF;

/** Date line for the October 20 Cortex Tools note. Earlier notes stay on UPDATES_AS_OF. */
export const CORTEX_TOOLS_AS_OF = 'October 20, 2026';

export const DELIVERY_CONTEXT =
  'That work still sits on sound data infrastructure: Terraform, CI/CD, dbt, agile delivery, Azure DevOps, GitHub Actions, and Airflow.';

/** Opening sentence on the /updates index. Also the page preview description. */
export const UPDATES_INDEX_LEDE =
  'Notes on Snowflake releases that change how a team runs governed AI on a semantic layer it owns, in its own account.';

/** Date line for the October 21 governance note. Earlier notes stay on UPDATES_AS_OF. */
export const GOVERNANCE_BY_DEFAULT_AS_OF = 'October 21, 2026';

/** Date line for the October 20 Fabric mirroring note. Earlier notes stay on UPDATES_AS_OF. */
export const FABRIC_MIRRORING_AS_OF = 'October 20, 2026';

export const UPDATE_SEO = {
  '/updates': {
    title: 'Snowflake updates | MyOrgLab',
    // Filled from the index lede once UPDATE_INDEX_CARDS exists.
    description: '',
  },
  '/updates/snowflake-cortex-tools-governed-agents': {
    title: 'Governed AI agents on Snowflake: what Cortex Tools means for your team | MyOrgLab',
    description:
      'Cortex Tools for Cortex AI Gateway is in preview. Governed MCP servers, tool allowlists, and an audit trail stay in your Snowflake account.',
  },
  '/updates/snowflake-2026-so-far': {
    title: 'Snowflake in 2026 so far | MyOrgLab',
    description:
      'How Snowflake agents, semantic views, and open formats changed in 2026, and what that means for governed AI on your semantic layer.',
  },
  '/updates/snowflake-governance-by-default': {
    title: 'Snowflake-provided tags and Trust Center sensitive data reports are GA | MyOrgLab',
    description:
      'Snowflake-provided tags in SNOWFLAKE.TAGS are generally available. Trust Center sensitive data reports require Enterprise Edition or higher.',
  },
  '/updates/behavior-change-bundles': {
    title: 'Behavior change bundles, explained | MyOrgLab',
    description:
      'How Snowflake behavior change bundles move through testing, opt-out, and generally enabled, and how to check status on your account.',
  },
  '/updates/snowflake-microsoft-fabric-mirroring-vs-iceberg': {
    title:
      'Mirroring vs. Iceberg interop: using Snowflake data in Microsoft Fabric (2026 guide) | MyOrgLab',
    description:
      'How to use Snowflake data in Microsoft Fabric: mirroring into OneLake versus Iceberg interop, including table types, cost, and security.',
  },
  '/updates/ossie-semantic-view-interchange': {
    title: 'Ossie and semantic view interchange | MyOrgLab',
    description:
      'Apache Ossie (Incubating) import and export for Snowflake semantic views is in preview, and what that means for your semantic layer.',
  },
  '/updates/snowflake-sqlalchemy-2-upgrade-checklist': {
    title: 'Upgrading to Snowflake SQLAlchemy 2.0 and Python Connector 5.0: a checklist | MyOrgLab',
    description:
      'Checklist for Snowflake SQLAlchemy 2.0.0 and Python Connector 5.0: requirements, breaking changes, and a non-prod upgrade path.',
  },
  '/updates/cortex-ai-gateway-azure': {
    title: 'Cortex AI Gateway lands on Azure: what it means for Power BI shops | MyOrgLab',
    description:
      'Cortex AI Gateway reached Azure on October 16, 2026 and is still in preview. Check region, grants, and model access in your account.',
  },
};

export const UPDATE_INDEX_CARDS = [
  {
    path: '/updates/snowflake-sqlalchemy-2-upgrade-checklist',
    tag: 'Drivers',
    asOf: SQLALCHEMY_UPGRADE_AS_OF,
    title: 'Upgrading to Snowflake SQLAlchemy 2.0 and Python Connector 5.0: a checklist',
    summary:
      'Snowflake SQLAlchemy 2.0.0 is a major release with breaking changes. Pin below 2.0.0, then test Python Connector 5.0 in a non-prod branch.',
  },
  {
    path: '/updates/cortex-ai-gateway-azure',
    tag: 'Azure',
    asOf: OCTOBER_19_NOTE_AS_OF,
    title: 'Cortex AI Gateway lands on Azure: what it means for Power BI shops',
    summary:
      'On October 16, 2026 Snowflake opened Cortex AI Gateway on Azure, GCP, and AWS New Zealand. It is still in public preview.',
  },
  {
    path: '/updates/snowflake-2026-so-far',
    tag: 'Agents',
    asOf: UPDATES_AS_OF,
    title: 'Snowflake in 2026 so far: agents, semantic views, open formats',
    summary:
      'Cortex Agents are the way in, the semantic view is the governed core, and Iceberg plus Fabric keep the tables open.',
  },
  {
    path: '/updates/behavior-change-bundles',
    tag: 'Account checks',
    asOf: UPDATES_AS_OF,
    title: 'Behavior change bundles, explained',
    summary:
      'How a monthly bundle moves from testing to opt-out to generally enabled, and the FACTS plus DIMENSIONS break in 2026_07.',
  },
  {
    path: '/updates/snowflake-governance-by-default',
    tag: 'Governance',
    asOf: GOVERNANCE_BY_DEFAULT_AS_OF,
    title: 'Snowflake-provided tags and Trust Center sensitive data reports are GA',
    summary:
      'Snowflake-provided tags in SNOWFLAKE.TAGS are generally available, including cost center, sensitivity, and environment. Trust Center entitlement and access reports require Enterprise Edition or higher.',
  },
  {
    path: '/updates/ossie-semantic-view-interchange',
    tag: 'Interchange',
    asOf: UPDATES_AS_OF,
    title: 'Ossie and semantic view interchange',
    summary:
      'Apache Ossie (Incubating) can move a semantic model in and out of a Snowflake semantic view. That interchange is in preview.',
  },
  {
    path: '/updates/snowflake-cortex-tools-governed-agents',
    tag: 'Governance',
    asOf: CORTEX_TOOLS_AS_OF,
    title: 'Governed AI agents on Snowflake: what Cortex Tools means for your team',
    summary:
      'Cortex Tools for Cortex AI Gateway is in preview. Admins allowlist tools per MCP server, and calls are reviewed with AGENT_TRACE_TABLE.',
  },
  {
    path: '/updates/snowflake-microsoft-fabric-mirroring-vs-iceberg',
    tag: 'Integration',
    asOf: FABRIC_MIRRORING_AS_OF,
    title: 'Mirroring vs. Iceberg interop: using Snowflake data in Microsoft Fabric (2026 guide)',
    summary:
      'Mirroring replicates Snowflake tables into OneLake. Apache Iceberg interop is generally available, so Snowflake and Fabric can share one copy.',
  },
];

UPDATE_SEO['/updates'].description = updatesIndexDescription(UPDATE_INDEX_CARDS, UPDATES_INDEX_LEDE);

/** GA and public-preview items only, one or a few per month, each with a source. */
export const SNOWFLAKE_2026_TIMELINE = [
  {
    month: 'January',
    items: [
      {
        date: 'Jan 12',
        iso: '2026-01-12',
        title: 'Custom instructions in semantic views',
        status: 'GA',
        summary:
          'AI_SQL_GENERATION and AI_QUESTION_CATEGORIZATION give Cortex Analyst instructions in the semantic view definition.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-01-12-semantic-views-custom-instructions',
        source: 'Jan 12 release note',
      },
      {
        date: 'Jan 30',
        iso: '2026-01-30',
        title: 'Two-way Iceberg access with Microsoft Fabric',
        status: 'GA',
        summary:
          'Fabric can query Snowflake-managed Iceberg tables, and Snowflake can query OneLake Iceberg tables through a REST catalog integration.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-01-30-iceberg-microsoft-fabric-bidirectional-data-access-ga',
        source: 'Jan 30 release note',
      },
    ],
  },
  {
    month: 'February',
    items: [
      {
        date: 'Feb 3',
        iso: '2026-02-03',
        title: 'Semantic View Autopilot',
        status: 'GA',
        summary:
          'Generates semantic views from query history and BI assets. Tableau was the first BI source named in the announcement.',
        href: 'https://www.snowflake.com/en/news/press-releases/snowflake-delivers-semantic-view-autopilot-as-the-foundation-for-trusted-scalable-enterprise-ready-AI/',
        source: 'Feb 3 press release',
      },
    ],
  },
  {
    month: 'March',
    items: [
      {
        date: 'Mar 2',
        iso: '2026-03-02',
        title: 'Standard SQL against a semantic view',
        status: 'GA',
        summary:
          'A semantic view can be queried by name in FROM with ordinary SELECT syntax, not only SEMANTIC_VIEW().',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-03-02-semantic-views-standard-sql',
        source: 'Mar 2 release note',
      },
      {
        date: 'Mar 13',
        iso: '2026-03-13',
        title: 'Cortex Agent evaluations',
        status: 'GA',
        summary: 'Evaluate agents with ground-truth and reference-free metrics.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-03-13-cortex-agent-evaluations',
        source: 'Mar 13 release note',
      },
    ],
  },
  {
    month: 'April',
    items: [
      {
        date: 'Apr 10',
        iso: '2026-04-10',
        title: 'Budgets for AI features',
        status: 'GA',
        summary:
          'Custom budgets can track and limit credits for AI Functions, CoCo, Cortex Agents, and CoWork by team.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-04-10-budgets-ai-features-ga',
        source: 'Apr 10 release note',
      },
      {
        date: 'Apr 20',
        iso: '2026-04-20',
        title: 'Cortex AI Guardrails',
        status: 'GA',
        summary:
          'Run-time protection against prompt injection and jailbreaks, initially for Cortex Code.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-04-20-cortex-ai-guardrails-ga',
        source: 'Apr 20 release note',
      },
    ],
  },
  {
    month: 'May',
    items: [
      {
        date: 'May 7',
        iso: '2026-05-07',
        title: 'Apache Iceberg v3',
        status: 'GA',
        summary: 'Iceberg v3 types and features are generally available.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-05-07-iceberg-v3-ga',
        source: 'May 7 release note',
      },
      {
        date: 'May 14',
        iso: '2026-05-14',
        title: 'Guardrails for CoWork and Cortex Agents',
        status: 'GA',
        summary: 'Prompt-injection and jailbreak protection extends to CoWork and Cortex Agents.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-05-14-cortex-ai-guardrails-si-cortex-agents',
        source: 'May 14 release note',
      },
      {
        date: 'May 26',
        iso: '2026-05-26',
        title: 'External engines write Snowflake-managed Iceberg',
        status: 'GA',
        summary:
          'External engines can read and write Snowflake-managed Iceberg v2 and v3 tables through Horizon Catalog.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-05-26-tables-iceberg-query-using-external-query-engine-snowflake-horizon-writes-ga',
        source: 'May 26 release note',
      },
    ],
  },
  {
    month: 'June',
    items: [
      {
        date: 'Jun 1',
        iso: '2026-06-01',
        title: 'Snowflake storage for Iceberg tables',
        status: 'GA',
        summary: 'Snowflake stores and manages Iceberg files without an external cloud volume to set up.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-06-01-iceberg-snowflake-storage-ga',
        source: 'Jun 1 release note',
      },
      {
        date: 'Jun 26',
        iso: '2026-06-26',
        title: 'SQL logical tables and variables',
        status: 'GA',
        summary:
          'A logical table in a semantic view can be a SQL query, and query-time variables can parameterize facts, dimensions, and metrics.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-06-26-semantic-views-inline-view-ga',
        source: 'Jun 26 release note',
      },
      {
        date: 'Jun 30',
        iso: '2026-06-30',
        title: 'Analytical search in Cortex Agents',
        status: 'Public preview',
        summary: 'Analytical queries over large document collections, beyond top-k retrieval.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-06-30-analytical-search-public-preview',
        source: 'Jun 30 release note',
      },
    ],
  },
  {
    month: 'July',
    items: [
      {
        date: 'Jul 7',
        iso: '2026-07-07',
        title: 'Deep Research in CoWork',
        status: 'GA',
        summary: 'A multi-step investigation mode in Snowflake CoWork for questions that take more than one pass.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-07-07-snowflake-cowork-deep-research-ga',
        source: 'Jul 7 release note',
      },
      {
        date: 'Jul 15',
        iso: '2026-07-15',
        title: 'Semantic view materializations',
        status: 'Public preview',
        summary:
          'Pre-aggregate dimensions and metrics with MAX_STALENESS. Shipped in server release 10.24.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/10_24',
        source: 'Server release 10.24',
      },
      {
        date: 'Jul 22',
        iso: '2026-07-22',
        title: 'External OAuth for MCP servers',
        status: 'GA',
        summary:
          'Bind a Snowflake-managed MCP server to your identity provider so a client reaches a governed agent with your roles.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-07-22-mcp-external-oauth',
        source: 'Jul 22 release note',
      },
    ],
  },
  {
    month: 'August',
    items: [
      {
        date: 'Aug 18',
        iso: '2026-08-18',
        title: 'Power BI ingestion for Semantic View Autopilot',
        status: 'GA',
        summary:
          'Upload a .pbit or .pbix file and Autopilot creates a semantic view from that model, including measures, table relationships, and column descriptions. Report-level measures and time-intelligence functions are not fully supported.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-08-18-semantic-views-power-bi-ingestion-ga',
        source: 'Aug 18 release note',
      },
      {
        date: 'Aug 26',
        iso: '2026-08-26',
        title: 'Semantic Studio',
        status: 'Public preview',
        summary: 'Author semantic views in Snowsight Workspaces, with CoCo, YAML, and Git.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-08-26-semantic-studio-preview',
        source: 'Aug 26 release note',
      },
      {
        date: 'Aug 30',
        iso: '2026-08-30',
        title: 'Cortex Agents asynchronous API',
        status: 'GA',
        summary: 'Run a long agent job in the background on AWS and Azure.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-08-30-cortex-agents-async-api-ga',
        source: 'Aug 30 release note',
      },
    ],
  },
  {
    month: 'September',
    items: [
      {
        date: 'Sep 15',
        iso: '2026-09-15',
        title: 'Cortex AI Gateway',
        status: 'Preview',
        summary: 'Govern how AI apps and third-party agents reach models.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-09-15-cortex-ai-gateway',
        source: 'Sep 15 release note',
      },
      {
        date: 'Sep 30',
        iso: '2026-09-30',
        title: 'Semantic Studio',
        status: 'GA',
        summary: 'Semantic view authoring in Workspaces, with Git-backed version control.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-09-30-semantic-studio-ga',
        source: 'Sep 30 Semantic Studio release note',
      },
      {
        date: 'Sep 30',
        iso: '2026-09-30',
        title: 'DCM DEFINE SEMANTIC VIEW',
        status: 'GA',
        summary:
          'Manage a semantic view, including AI instructions and verified queries, declaratively in a DCM project.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-09-30-dcm-define-semantic-view-ga',
        source: 'Sep 30 DCM release note',
      },
    ],
  },
  {
    month: 'October',
    items: [
      {
        date: 'Oct 5',
        iso: '2026-10-05',
        title: 'Cortex Agent code execution tool',
        status: 'GA',
        summary: 'An agent can run code during a conversation to process data and calculate.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-05-cortex-agents-code-execution-tool-ga',
        source: 'Oct 5 release note',
      },
      {
        date: 'Oct 12',
        iso: '2026-10-12',
        title: 'Horizon Context',
        status: 'Preview',
        summary:
          'column-level lineage across 18 connectors incl. Power BI; measure/visual depth not documented',
        links: [
          {
            href: 'https://docs.snowflake.com/en/user-guide/snowflake-horizon/horizon-context',
            source: 'Horizon Context',
          },
          {
            href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-12-horizon-context-preview',
            source: 'Oct 12 release note',
          },
        ],
      },
      {
        date: 'Oct 13',
        iso: '2026-10-13',
        title: 'Cortex Agent SDK',
        status: 'Public preview',
        summary: 'Python and TypeScript SDK for applications that run Cortex Agents and CoCo.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-13-cortex-agent-sdk-preview',
        source: 'Oct 13 release note',
      },
      {
        date: 'Oct 13',
        iso: '2026-10-13',
        title: 'Gemini 4 Argon in Cortex AI',
        status: 'Public preview',
        summary:
          'Available in Cortex AI Functions. Snowflake lists CoWork, CoCo, and Cortex Agents as coming soon, on supported clouds, using cross-region inference.',
        href: 'https://www.snowflake.com/en/blog/gemini-4-argon-snowflake-cortex-ai/',
        source: 'Oct 13 Snowflake blog',
      },
      {
        date: 'Oct 14',
        iso: '2026-10-14',
        title: 'Document generation in Snowflake CoWork',
        status: 'GA',
        summary:
          'CoWork can generate a PDF, a Word document (.docx), a PowerPoint presentation, or an HTML file. Generation requires the Cortex Agent code execution tool, and the file can be viewed inline.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-14-cowork-document-generation-ga',
        source: 'Oct 14 release note',
      },
      {
        date: 'Oct 15',
        iso: '2026-10-15',
        title: 'Postgres data mirroring',
        status: 'GA',
        summary: 'Data mirroring for Snowflake Postgres is generally available on AWS and Azure.',
        href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-15-postgres-data-mirroring-ga',
        source: 'Oct 15 release note',
      },
    ],
  },
];

export const SNOWFLAKE_2026_SOURCES = [
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-04-13-cortex-agents-agentic-analyst',
    label: 'Apr 13: Agents generate SQL from semantic views',
  },
  {
    href: 'https://www.snowflake.com/en/news/press-releases/snowflake-cowork-powers-the-agentic-enterprise-as-the-personal-agent-for-knowledge-workers-to-work-smarter/',
    label: 'Jun 2: Snowflake CoWork announcement',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-08-28-cortex-analyst-transition-cortex-agents',
    label: 'Aug 28: Move from Cortex Analyst to Cortex Agents',
  },
  {
    href: 'https://www.snowflake.com/en/blog/gemini-4-argon-snowflake-cortex-ai/',
    label: 'Oct 13: Gemini 4 Argon in public preview on Cortex AI',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-14-cowork-document-generation-ga',
    label: 'Oct 14: Document generation in Snowflake CoWork (GA)',
  },
  {
    href: 'https://www.snowflake.com/en/news/press-releases/snowflake-delivers-semantic-view-autopilot-as-the-foundation-for-trusted-scalable-enterprise-ready-AI/',
    label: 'Feb 3: Semantic View Autopilot',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-08-18-semantic-views-power-bi-ingestion-ga',
    label: 'Aug 18: Power BI ingestion for Semantic View Autopilot (GA)',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-09-30-semantic-studio-ga',
    label: 'Sep 30: Semantic Studio (GA)',
  },
  {
    href: 'https://docs.snowflake.com/en/user-guide/views-semantic/semantic-studio',
    label: 'Semantic Studio user guide',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-09-30-dcm-define-semantic-view-ga',
    label: 'Sep 30: DEFINE SEMANTIC VIEW (GA)',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-05-07-iceberg-v3-ga',
    label: 'May 7: Apache Iceberg v3 (GA)',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-01-30-iceberg-microsoft-fabric-bidirectional-data-access-ga',
    label: 'Jan 30: Two-way Iceberg access with Microsoft Fabric (GA)',
  },
  {
    href: 'https://docs.snowflake.com/en/user-guide/snowflake-horizon/horizon-context',
    label: 'Horizon Context',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-12-horizon-context-preview',
    label:
      'Oct 12: Horizon Context preview. column-level lineage across 18 connectors incl. Power BI; measure/visual depth not documented',
  },
];

export const BEHAVIOR_BUNDLE_SOURCES = [
  {
    href: 'https://docs.snowflake.com/en/release-notes/intro-bcr-releases',
    label: 'Behavior change release overview',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/bcr-bundles/managing-behavior-change-releases',
    label: 'Managing behavior change bundles',
  },
  {
    href: 'https://docs.snowflake.com/en/sql-reference/functions/system_behavior_change_bundle_status',
    label: 'SYSTEM$BEHAVIOR_CHANGE_BUNDLE_STATUS',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/bcr-bundles/2026_07/bcr-2287',
    label: 'BCR-2287: Querying FACTS with DIMENSIONS',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/10_37',
    label: 'Server release 10.37',
  },
  {
    href: 'https://docs.snowflake.com/en/sql-reference/functions/system_show_active_behavior_change_bundles',
    label: 'SYSTEM$SHOW_ACTIVE_BEHAVIOR_CHANGE_BUNDLES',
  },
];

export const CORTEX_GATEWAY_SOURCES = [
  {
    href: 'https://docs.snowflake.com/en/release-notes/all-release-notes',
    label: 'Oct 16: Cortex AI Gateway on Azure, GCP, and AWS New Zealand',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-16-cortex-ai-gateway-regions',
    label: 'Oct 16 release note: Cortex AI Gateway regions',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-09-15-cortex-ai-gateway',
    label: 'Sep 15: Cortex AI Gateway (Preview)',
  },
];

export const SQLALCHEMY_UPGRADE_SOURCES = [
  {
    href: 'https://docs.snowflake.com/en/release-notes/clients-drivers/sqlalchemy-2026',
    label: 'Oct 19: Snowflake SQLAlchemy 2.0.0',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/clients-drivers/python-connector-2026',
    label: 'Python Connector 5.0.0 requires Python 3.11 or later',
  },
];

export const OSSIE_SOURCES = [
  {
    href: 'https://ossie.apache.org/updates/ossie-enters-apache-incubator/',
    label: 'Jul 10: Apache Ossie enters the Apache Incubator',
  },
  {
    href: 'https://docs.snowflake.com/en/sql-reference/stored-procedures/system_create_semantic_view_from_ossie_yaml',
    label: 'SYSTEM$CREATE_SEMANTIC_VIEW_FROM_OSSIE_YAML',
  },
  {
    href: 'https://docs.snowflake.com/en/sql-reference/functions/system_read_ossie_yaml_from_semantic_view',
    label: 'SYSTEM$READ_OSSIE_YAML_FROM_SEMANTIC_VIEW',
  },
];

export const GOVERNANCE_BY_DEFAULT_SOURCES = [
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-20-snowflake-provided-tags-ga',
    label: 'Oct 20, 2026: Snowflake-provided tags (General availability)',
  },
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-20-sensitive-data-entitlement-access-reports-ga',
    label: 'Oct 20, 2026: Sensitive Data Entitlement and Access reports (General availability)',
  },
];

export const CORTEX_TOOLS_SOURCES = [
  {
    href: 'https://docs.snowflake.com/en/release-notes/2026/other/2026-10-19-cortex-ai-gateway-tools',
    label: 'Oct 19: Cortex Tools for Cortex AI Gateway (Preview)',
  },
];
